import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  setFormData,
  updateFormData,
  setStructureType,
  setGeneratedCode,
  setCurrentStep,
  nextStep,
  previousStep,
  resetForm,
  markAsSaved,
  setComment,
} from "@/store/slices/codeGenerationSlice";
import type { SilaeFormData, StructureType } from "@/types/silae";
import type { RootState } from "@/store";

/**
 * Hook personnalisé pour gérer l'état de génération de code SILAE
 * Fournit un accès facile aux données du wizard et aux actions Redux
 *
 * @returns {Object} État et actions pour la génération de code
 */
export const useCodeGeneration = () => {
  const dispatch = useAppDispatch();

  // Sélectionner l'état depuis Redux
  const codeGenerationState = useAppSelector(
    (state: RootState) => state.codeGeneration,
  );

  // ========================================================================
  // ACTIONS POUR LES DONNÉES DU FORMULAIRE
  // ========================================================================

  /**
   * Remplace complètement les données du formulaire
   */
  const handleSetFormData = useCallback(
    (data: SilaeFormData) => {
      dispatch(setFormData(data));
    },
    [dispatch],
  );

  /**
   * Met à jour partiellement les données du formulaire
   */
  const handleUpdateFormData = useCallback(
    (data: Partial<SilaeFormData>) => {
      dispatch(updateFormData(data));
    },
    [dispatch],
  );

  /**
   * Change le type de structure et nettoie les données incompatibles
   */
  const handleSetStructureType = useCallback(
    (structureType: StructureType) => {
      dispatch(setStructureType(structureType));
    },
    [dispatch],
  );

  // ========================================================================
  // ACTIONS POUR LE CODE GÉNÉRÉ
  // ========================================================================

  /**
   * Définit le code généré
   */
  const handleSetGeneratedCode = useCallback(
    (code: string) => {
      dispatch(setGeneratedCode(code));
    },
    [dispatch],
  );

  // ========================================================================
  // ACTIONS POUR LA NAVIGATION DU WIZARD
  // ========================================================================

  /**
   * Change l'étape actuelle du wizard
   */
  const handleSetCurrentStep = useCallback(
    (step: number) => {
      dispatch(setCurrentStep(step));
    },
    [dispatch],
  );

  /**
   * Passe à l'étape suivante du wizard
   */
  const handleNextStep = useCallback(() => {
    dispatch(nextStep());
  }, [dispatch]);

  /**
   * Retourne à l'étape précédente du wizard
   */
  const handlePreviousStep = useCallback(() => {
    dispatch(previousStep());
  }, [dispatch]);

  // ========================================================================
  // ACTIONS DE RÉINITIALISATION
  // ========================================================================

  /**
   * Réinitialise complètement le formulaire
   */
  const handleResetForm = useCallback(() => {
    dispatch(resetForm());
  }, [dispatch]);

  /**
   * Marque le formulaire comme sauvegardé (non modifié)
   */
  const handleMarkAsSaved = useCallback(() => {
    dispatch(markAsSaved());
  }, [dispatch]);

  /**
   * Met à jour le commentaire libre du diagramme.
   */
  const handleSetComment = useCallback(
    (value: string) => {
      dispatch(setComment(value));
    },
    [dispatch],
  );

  // ========================================================================
  // RETOUR DE L'OBJET AVEC ÉTAT ET ACTIONS
  // ========================================================================

  return {
    // État
    formData: codeGenerationState.formData,
    generatedCode: codeGenerationState.generatedCode,
    currentStep: codeGenerationState.currentStep,
    isModified: codeGenerationState.isModified,

    // Actions - Données du formulaire
    setFormData: handleSetFormData,
    updateFormData: handleUpdateFormData,
    setStructureType: handleSetStructureType,

    // Actions - Code généré
    setGeneratedCode: handleSetGeneratedCode,

    // Actions - Navigation
    setCurrentStep: handleSetCurrentStep,
    nextStep: handleNextStep,
    previousStep: handlePreviousStep,

    // Actions - Réinitialisation
    resetForm: handleResetForm,
    markAsSaved: handleMarkAsSaved,

    // Actions - Commentaire diagramme
    setComment: handleSetComment,
  };
};
